import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/auth/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { QUERY_CONFIG } from '@/config/constants';
import { QUERY_KEYS } from '@/config/queries';
import { fetchDashboardData } from '@/lib/data/dashboard';
import { fetchLeaderboardData } from '@/lib/data/leaderboard';
import { fetchOwnedCoupons, fetchRewardStock, fetchRewardsData } from '@/lib/data/rewards';
import { useCallback } from 'react';
import { createLogger } from '@/lib/structuredLogger';

const logger = createLogger('usePrefetch');

export const usePrefetch = () => {
  const queryClient = useQueryClient();
  const { user, session } = useAuth();

  const prefetchProfile = useCallback(async () => {
    if (!user) return;

    // Prefetch profile data - matches Profile.tsx query structure exactly
    await queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.PROFILE(user.id),
      queryFn: async () => {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          logger.error('Error loading profile:', error);
          throw error;
        }

        if (data) {
          return data;
        } else {
          // Ensure profile via secure RPC (bypasses RLS safely) - matches Profile.tsx
          const fallbackEmail = user.email ?? `${user.id}@user.local`;
          const { data: ensured, error: ensureError } = await supabase.rpc('ensure_profile', {
            p_email: fallbackEmail,
          });

          if (ensureError) {
            logger.error('Ensure profile error:', ensureError);
            // Return default values instead of throwing
            return {
              id: user.id,
              email: fallbackEmail,
              department: '',
              age: 0,
              consent_data_processing: false,
              consent_ai_tips: false,
              consent_anonymous_insights: false,
            };
          }

          // Mark as newly created for toast handling
          return { ...ensured, _isNew: true };
        }
      },
      staleTime: QUERY_CONFIG.STALE_TIME.LONG, // 5 minutes
      gcTime: QUERY_CONFIG.GC_TIME.MEDIUM, // 10 minutes cache
    });

    // Prefetch company data
    await queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.USER_COMPANY(user.id),
      queryFn: async () => {
        const { data, error } = await supabase
          .from('company_employees')
          .select(
            `
            company_id,
            companies!inner(
              id,
              name,
              display_name
            )
          `,
          )
          .eq('user_id', user.id)
          .eq('is_active', true)
          .maybeSingle();

        if (error) {
          logger.error('Error loading company:', error);
          return null;
        }

        return data;
      },
      staleTime: QUERY_CONFIG.STALE_TIME.LONG, // 5 minutes
      gcTime: QUERY_CONFIG.GC_TIME.MEDIUM, // 10 minutes cache
    });

    // Prefetch MFA status
    await queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.MFA_FACTORS(user.id),
      queryFn: async () => {
        const { data } = await supabase.auth.mfa.listFactors();
        return data;
      },
      staleTime: QUERY_CONFIG.STALE_TIME.LONG,
      gcTime: QUERY_CONFIG.GC_TIME.MEDIUM,
    });
  }, [user, queryClient]);

  const prefetchDashboard = useCallback(async () => {
    if (!user || !session) return;

    const today = new Date().toISOString().split('T')[0];

    // Prefetch dashboard data using batched RPC (75% faster than 4 separate queries)
    await queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.DASHBOARD(user.id),
      queryFn: () => fetchDashboardData(user.id, session, today),
      staleTime: QUERY_CONFIG.STALE_TIME.LONG, // 5 minutes
      gcTime: QUERY_CONFIG.GC_TIME.MEDIUM, // 10 minutes cache
    });
  }, [user, session, queryClient]);

  const prefetchRewards = useCallback(async () => {
    if (!user || !session) return;

    await queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.REWARDS_DATA(user.id),
      queryFn: () => fetchRewardsData(user.id, session),
      staleTime: QUERY_CONFIG.STALE_TIME.LONG,
      gcTime: QUERY_CONFIG.GC_TIME.MEDIUM,
    });

    await queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.REWARD_STOCK(),
      queryFn: fetchRewardStock,
      staleTime: QUERY_CONFIG.STALE_TIME.LONG,
      gcTime: QUERY_CONFIG.GC_TIME.MEDIUM,
    });

    await queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.OWNED_COUPONS(user.id),
      queryFn: () => fetchOwnedCoupons(user.id),
      staleTime: QUERY_CONFIG.STALE_TIME.LONG,
      gcTime: QUERY_CONFIG.GC_TIME.MEDIUM,
    });
  }, [queryClient, session, user]);

  const prefetchLeaderboard = useCallback(async () => {
    if (!user || !session) return;

    // Prefetch leaderboard data using batched RPC (same as useLeaderboardData)
    await queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.LEADERBOARD_DATA(user.id),
      queryFn: () => fetchLeaderboardData(user.id, session),
      staleTime: QUERY_CONFIG.STALE_TIME.LONG,
      gcTime: QUERY_CONFIG.GC_TIME.MEDIUM,
    });
  }, [user, session, queryClient]);

  return {
    prefetchProfile,
    prefetchDashboard,
    prefetchRewards,
    prefetchLeaderboard,
  };
};
